// weatherPresentation.js — turn the backend's presentation-free weather atoms
// (var / dir / pct / band / kind) into labels, icons and plain-language copy.
// Framework-free so it can be unit-tested without mounting components, and so the
// backend stays number-firewalled (it ships facts; presentation lives here).
//
// Atom shape (from /weather_insight/):
//   { zone, var, dir:'up'|'down', pct:int, band:'established'|'watch', kind, lag, phrase }

import { i18n, currentLanguage } from '@/i18n'

// Display text comes from locales/<lang>/weather.json (keys under `weather.`); the
// English there is exactly the English that used to live in this file.
const tr = (key, params, plural) => (plural === undefined
  ? i18n.global.t(`weather.${key}`, params || {})
  : i18n.global.t(`weather.${key}`, params || {}, plural))

// Per-variable icon + the noun we use when naming it as a driver.
const VAR_META = {
  temp:     { icon: 'bi-thermometer-sun', up: 'Warmer',   down: 'Cooler' },
  precip:   { icon: 'bi-cloud-rain',      up: 'Rain',     down: 'Drier'  },
  wind:     { icon: 'bi-wind',            up: 'Wind',     down: 'Calm'   },
  cloud:    { icon: 'bi-clouds',          up: 'Cloud',    down: 'Bright' },
  snow:     { icon: 'bi-snow',            up: 'Snow',     down: 'Snow-free' },
  sunshine: { icon: 'bi-sun',             up: 'Sun',      down: 'Dull'   },
}

// The weather CONDITION that brings MORE visits, as an adjective (dir already
// encodes the effect on footfall/share, so 'up' = busier when the var is higher).
const BUSIER_ADJ = {
  temp:     { up: 'warm',     down: 'cool' },
  precip:   { up: 'wet',      down: 'dry' },
  wind:     { up: 'windy',    down: 'calm' },
  cloud:    { up: 'overcast', down: 'bright' },
  snow:     { up: 'snowy',    down: 'snow-free' },
  sunshine: { up: 'sunny',    down: 'dull' },
}

export function varIcon(v) {
  return (VAR_META[v] || {}).icon || 'bi-cloud'
}

// Short driver label for a chip, e.g. temp+ -> "Warmer", precip- -> "Drier".
export function driverLabel(atom) {
  const m = VAR_META[atom.var]
  if (!m) return atom.var || tr('driver.fallback')
  return tr(`driver.${atom.var}.${atom.dir === 'up' ? 'up' : 'down'}`)
}

// Signed percent as a display string, e.g. "+9%" / "−6%" (real minus glyph).
export function signedPct(atom) {
  return `${atom.dir === 'up' ? '+' : '−'}${atom.pct}%`
}

// Compose the store signature sub-line from up to two drivers:
//   "runs busier in warm, dry weather".
export function signaturePhrase(drivers, storeName) {
  const adjs = (drivers || [])
    .slice(0, 2)
    .map((d) => ((BUSIER_ADJ[d.var] || {})[d.dir] ? tr(`busierAdj.${d.var}.${d.dir}`) : null))
    .filter(Boolean)
  const who = storeName ? tr('signature.storeRuns', { store: storeName }) : tr('signature.yourStoreRuns')
  if (!adjs.length) return tr('signature.steady', { who })
  return tr('signature.busier', { who, adjs: adjs.join(', ') })
}

// Plain-language line for a per-zone signal, e.g. "busier as it warms",
// "quieter when wet", with an anticipatory prefix when the effect leads the weather.
export function zoneShortText(atom) {
  const warmerCooler = {
    temp:     { up: 'busier as it warms',     down: 'busier when cooler' },
    precip:   { up: 'busier on wet days',     down: 'quieter when wet' },
    wind:     { up: 'busier on windy days',   down: 'quieter when windy' },
    cloud:    { up: 'busier under cloud',     down: 'busier in bright weather' },
    snow:     { up: 'busier when it snows',   down: 'quieter when it snows' },
    sunshine: { up: 'busier in sunshine',     down: 'quieter in sunshine' },
  }
  const base = ((warmerCooler[atom.var] || {})[atom.dir])
    ? tr(`zoneText.${atom.var}.${atom.dir}`) : tr('zoneText.fallback')
  if (atom.kind === 'anticipatory') {
    const days = Math.abs(atom.lag || 1)
    return tr('zoneText.ahead', { base, from: days, to: days + 1 }, days)
  }
  return base
}

// Compose the Actions "timely heads-up" from the nightly outlook (weather_signature
// .outlook). Returns { id, title, text, validThrough } or null. The strip self-expires
// past validThrough and dismissal is scoped to `id` (one weather episode).
export function outlookHeadsUp(outlook) {
  if (!outlook || !outlook.condition) return null
  const cond = outlook.condition
  // The backend ships the Dutch condition words too (condition_nl); the English
  // condition stays the identity of the episode (id).
  const shown = (currentLanguage() === 'nl' && outlook.condition_nl) ? outlook.condition_nl : cond
  const Cond = shown.charAt(0).toUpperCase() + shown.slice(1)
  const busier = outlook.direction === 'busier'
  const title = tr('outlook.title', { cond: Cond })
  let text
  if (outlook.lead_zone) {
    text = busier
      ? tr('outlook.busierZone', { zone: outlook.lead_zone, cond: shown })
      : tr('outlook.quieterZone', { zone: outlook.lead_zone, cond: shown })
  } else {
    text = busier ? tr('outlook.busierStore') : tr('outlook.quieterStore')
  }
  return {
    id: outlook.id || `wx:${outlook.direction}:${cond}`,
    title,
    text,
    validThrough: outlook.valid_through || null,
  }
}

export function bandLabel(band) {
  return band === 'established' ? tr('band.established') : band === 'watch' ? tr('band.watch') : tr('band.none')
}

export function bandTip(band) {
  return band === 'established'
    ? tr('band.tipEstablished')
    : band === 'watch'
      ? tr('band.tipWatch')
      : tr('band.tipNone')
}

// A temperature bucket for the "visits by temperature" curve tone.
export function tempTone(celsius) {
  if (celsius == null) return ''
  if (celsius <= 20) return 'cold'
  if (celsius >= 27) return 'hot'
  return ''
}

// The shown name of a temperature bucket label ('Cold' … 'Warm'), which is data.
export function tempLabel(label) {
  const key = `weather.temp.${label}`
  return i18n.global.te(key) ? i18n.global.t(key) : label
}

// Build the "visits by temperature" mini-curve from a zone's real daily counts and
// the store's daily daytime-temperature series. Splits shared days into temperature
// QUARTILES (scale-free per store — matches the demo's relative Cold→Warm framing)
// and averages visits within each. Returns [{label,h,tone}] (h = 0..100), or null
// when there aren't enough overlapping days to be meaningful.
export function bucketVisitsByTemp(countsByDate, tempByDate, { minDays = 8 } = {}) {
  if (!countsByDate || !tempByDate) return null
  const pairs = []
  for (const [d, c] of Object.entries(countsByDate)) {
    const t = tempByDate[d]
    if (t != null && c != null) pairs.push({ t: +t, c: +c })
  }
  if (pairs.length < minDays) return null
  pairs.sort((a, b) => a.t - b.t)
  const labels = ['Cold', 'Cool', 'Mild', 'Warm']
  const n = pairs.length
  const avgs = []
  for (let q = 0; q < 4; q++) {
    const lo = Math.floor((q * n) / 4)
    const hi = Math.floor(((q + 1) * n) / 4)
    const slice = pairs.slice(lo, Math.max(hi, lo + 1))
    avgs.push(slice.reduce((s, p) => s + p.c, 0) / slice.length)
  }
  const max = Math.max(...avgs, 1)
  return avgs.map((a, i) => ({
    label: labels[i],
    h: Math.round((a / max) * 100),
    tone: i === 0 ? 'cold' : i === 3 ? 'hot' : '',
  }))
}
